import React, { useMemo, useEffect, useState, useCallback, useRef } from "react";
import {
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import RichTextEditor from "components/Shared/RichTextEditor";
import { useAuth } from "hooks";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";
import { useListGlobalListQuery } from "store/services/globalListApi";
import { useListListQuery } from "store/services/listService";
import { GLOBAL_LIST_CONSTANTS } from "data/static/globalListOptions";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useLazyShipmentForDeliveryCollectionSearchQuery } from "store/services/deliveryApi";
import { getSocketInstance } from "socket/socket.io";
import NCHLQR from "utils/generateNCHLQRCode";

const DeliveryCollectionByShipmentForm = ({
  isBusy = null,
  deliveryCollectionDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin, loggedInUser } = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      account: viewEntity?.entity?.account || "",
      entity: viewEntity?.entity || "",
      deliveryType: "",
      driver: "",
      vehicle: "",
      responsible: "",
      remittanceRemarks: "",
      shipment: null,
      priceIds: [],
      collectionMode: "",
      bankName: "",
      actualAmount: 0,
      collectedAmount: 0,
      notes: "<p>Enter Notes.</p>",
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchDeliveryType = useWatch({ control, name: "deliveryType" });
  const _watchShipment = useWatch({ control, name: "shipment" });
  const _watchPriceIds = useWatch({ control, name: "priceIds" });
  const _watchActualAmount = useWatch({ control, name: "actualAmount" });
  const _watchTransferMode = useWatch({ control, name: "collectionMode" });
  const _watchCollectedAmount = useWatch({ control, name: "collectedAmount" });
  const { data: entityTypeList = [] } = useListListQuery(
    {
      type: ENTITY_LIST_CONSTANTS.PAYMENT_METHOD,
      status: "Active",
    },
    { skip: false, refetchOnMountOrArgChange: true }
  );
  const _mappedAccountTypeList = useMemo(
    () =>
      Array.isArray(entityTypeList)
        ? entityTypeList.map((item) => ({
          label: item?.name || "",
          value: item?._id || "",
        }))
        : [],
    [entityTypeList]
  );
  const selectedCollectionMethod = useMemo(() => {
    if (!Array.isArray(entityTypeList) || !_watchTransferMode) return null;
    return entityTypeList.find((item) => item._id === _watchTransferMode);
  }, [_watchTransferMode, entityTypeList]);
  const isNCHL = selectedCollectionMethod?.name === "NCHL";
  const { data: bankNameOptions, isLoading: loadingBankNameOptions } =
    useListGlobalListQuery({
      type: GLOBAL_LIST_CONSTANTS.BANK_NAME,
      isSubType: false,
      includeChildren: true,
      status: "Active",
    });
  const formattedBankNameOptions = useMemo(
    () =>
      bankNameOptions?.map((option) => ({
        value: option._id,
        label: option.name,
      })) || [],
    [bankNameOptions]
  );
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [
    { data: userList, isLoading: loadingUsers, isFetching: fetchingUsers },
  ] = useLazyGetDDLSystemUserListQuery();
  const [
    fetchShipments,
    { data: shipments, isLoading: loadingShipments, error: shipmentsError },
  ] = useLazyShipmentForDeliveryCollectionSearchQuery();

  const [pricingData, setPricingData] = useState([]);
  const [selectedPriceIds, setSelectedPriceIds] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [invalidShipments, setInvalidShipments] = useState([]);
  const [localShipments, setLocalShipments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [allFetchedShipments, setAllFetchedShipments] = useState([]);
  const nchlQRRef = useRef(null);
  const paymentSuccessHandled = useRef(false);
  const [isPaymentDone, setIsPaymentDone] = useState(false);
  const [paymentResponseData, setPaymentResponseData] = useState(null);
  const [currentTraceId, setCurrentTraceId] = useState(null);
  const _mappedUsers = useMemo(
    () =>
      userList?.map((el) => ({
        label: el?.name,
        id: el?._id,
      })) || [],
    [userList]
  );

  // Inside useMemo for availableShipments
  const availableShipments = useMemo(
    () =>
      allFetchedShipments.map((delivery) => ({
        _id: delivery.shipmentMongoId,
        shipmentId: delivery.shipmentId,
        fulfillmentCompletionId: delivery.fulfillmentCompletionId,
        priceItems: delivery.priceItems || [],
        driverId: delivery.driverId,
        driverName: delivery.driverName,
        vehicle_id: delivery.vehicle_id,
        vehicleId: delivery.vehicleId,
        responsibleUser: delivery.responsibleUser,
        userName: delivery.userName,
        sourceBranch: delivery.sourceBranch,
        destinationBranch: delivery.destinationBranch,
      })),
    [allFetchedShipments]
  );

  // Handle price item selection
  // Handle price item selection
  const handlePriceSelection = (price) => {
    const isSelected = selectedPriceIds.includes(price._id);
    let updatedPriceIds;

    if (isSelected) {
      updatedPriceIds = selectedPriceIds.filter((id) => id !== price._id);
    } else {
      updatedPriceIds = [...selectedPriceIds, price._id];
    }

    setSelectedPriceIds(updatedPriceIds);

    const updatedPrices = pricingData
      .filter((item) => updatedPriceIds.includes(item.price._id))
      .map((item) => item.price);

    setValue("priceIds", updatedPrices);

    // Update amounts immediately
    const totalAmount = updatedPrices.reduce((sum, priceItem) => {
      return sum + (priceItem.charge || 0);
    }, 0);

    setValue("actualAmount", totalAmount);
    setValue("collectedAmount", totalAmount);
  };

  // Process pricing data for selected shipments
  useEffect(() => {
    if (shipmentsError) {
      toast.error("Failed to load shipments. Please try again.");
    }

    const hasShipmentSelection = isNCHL
      ? !!_watchShipment?._id
      : Array.isArray(_watchShipment) && _watchShipment.length > 0;
    if (
      hasShipmentSelection &&
      Array.isArray(allFetchedShipments) &&
      allFetchedShipments.length > 0
    ) {
      const selectedShipmentIds = isNCHL
        ? [_watchShipment._id]
        : _watchShipment.map((s) => s._id);

      const filteredPriceItems = allFetchedShipments
        .filter((delivery) =>
          selectedShipmentIds.includes(delivery.shipmentMongoId)
        )
        .flatMap((delivery) =>
          (delivery.priceItems || []).map((price) => ({
            shipmentId: delivery.shipmentId,
            price: {
              _id: price._id,
              feeItem: price.feeItem || "Unknown",
              charge: price.charge || 0,
              chargeType: price.chargeType || "Charge",
              payer: price.payer || "Unknown",
              paymentLocation: price.paymentLocation || "Unknown",
              user: price.responsibleUser || "Unknown",
              createdAt: price.createdAt || new Date(),
              shipmentId: price.shipmentId || delivery.shipmentId,
              sourceBranch: delivery.sourceBranch || "",
              destinationBranch: delivery.destinationBranch || "",
              paymentStatus: price.paymentStatus || "Not Paid",
            },
          }))
        );

      setPricingData(filteredPriceItems);
      if (deliveryCollectionDetail && filteredPriceItems.length > 0) {
        const cocPriceIds = (deliveryCollectionDetail.priceIds || []).map(
          (p) => p._id || p
        );
        const matchingPriceIds = filteredPriceItems
          .filter((item) => cocPriceIds.includes(item.price._id))
          .map((item) => item.price._id);
        setSelectedPriceIds(matchingPriceIds);
        setValue(
          "priceIds",
          filteredPriceItems
            .filter((item) => cocPriceIds.includes(item.price._id))
            .map((item) => item.price)
        );
      } else if (!deliveryCollectionDetail && filteredPriceItems.length > 0) {
        const allIds = filteredPriceItems.map((item) => item.price._id);
        setSelectedPriceIds(allIds);
        setValue(
          "priceIds",
          filteredPriceItems.map((item) => item.price)
        );
      }
    } else if (!hasShipmentSelection) {
      setPricingData([]);
      setSelectedPriceIds([]);
      setValue("priceIds", []);
      setInvalidShipments([]);
      setOpenDialog(false);
    }
  }, [
    _watchShipment,
    allFetchedShipments,
    deliveryCollectionDetail,
    setValue,
    shipmentsError,
    isNCHL,
  ]);

  // Auto-populate deliveryType, driver, vehicle, or responsible based on shipment selection
  useEffect(() => {
    const hasShipmentSelection = isNCHL
      ? !!_watchShipment?._id
      : Array.isArray(_watchShipment) && _watchShipment.length > 0;

    if (
      hasShipmentSelection &&
      Array.isArray(allFetchedShipments) &&
      allFetchedShipments.length > 0
    ) {
      const selectedShipmentMongoId = isNCHL
        ? _watchShipment._id
        : _watchShipment[0]?._id;

      const selectedShipment = allFetchedShipments.find(
        (delivery) => delivery.shipmentMongoId === selectedShipmentMongoId
      );

      if (selectedShipment) {
        if (selectedShipment.driverId || selectedShipment.vehicle_id) {
          setValue("deliveryType", "Home Delivery");
          setValue("driver", {
            value: selectedShipment.driverId || "",
            label:
              selectedShipment.driverName ||
              selectedShipment.driverId ||
              "Unknown Driver",
          });
          setValue("vehicle", {
            value: selectedShipment.vehicle_id || "",
            label:
              selectedShipment.vehicleId ||
              selectedShipment.vehicle_id ||
              "Unknown Vehicle",
          });
          setValue("responsible", "");
        } else if (selectedShipment.responsibleUser) {
          setValue("deliveryType", "Branch Delivery");
          setValue("responsible", {
            id: selectedShipment.responsibleUser || "",
            label:
              selectedShipment.userName ||
              selectedShipment.responsibleUser ||
              "Unknown User",
          });
          setValue("driver", "");
          setValue("vehicle", "");
        } else {
          setValue("deliveryType", "Free Unloading Delivery");
          setValue("driver", "");
          setValue("vehicle", "");
          setValue("responsible", "");
        }
      }
    } else if (!hasShipmentSelection) {
      setValue("deliveryType", "");
      setValue("driver", "");
      setValue("vehicle", "");
      setValue("responsible", "");
    }
  }, [_watchShipment, allFetchedShipments, setValue, isNCHL]);

  // Update actual and collected amounts based on selected priceIds
  useEffect(() => {
    if (_watchPriceIds?.length > 0) {
      const totalAmount = _watchPriceIds.reduce((sum, price) => {
        return sum + (price.charge || 0);
      }, 0);
      setValue("actualAmount", totalAmount);
      setValue("collectedAmount", totalAmount);
    } else {
      setValue("actualAmount", 0);
      setValue("collectedAmount", 0);
    }
  }, [_watchPriceIds, setValue]);

  const unpaidPriceIds = useMemo(() => {
    if (!_watchPriceIds?.length) return [];
    return _watchPriceIds
      .filter((price) => price.paymentStatus !== "Paid")
      .map((price) => price._id)
      .filter(Boolean);
  }, [_watchPriceIds]);

  const handleNCHLPaymentSuccess = useCallback(
    (response) => {
      // Reentrancy guard. `handleNCHLPaymentSuccess` can fire from two
      // independent sources for the same payment: (1) NCHLQR's onSuccess
      // prop when debitStatus === "000", and (2) the parent's socket
      // listener on `payment-success/delivery-collection/${currentTraceId}`.
      // Both can arrive close together — without this guard we toast and
      // navigate twice. Reset in the priceIds-watcher effect below so a
      // new payment attempt can notify again.
      if (paymentSuccessHandled.current) return;
      paymentSuccessHandled.current = true;
      let paymentData = null;
      let deliveryCollectionId = null;
      let redirectUrl = null;

      if (response?.deliveryCollectionId) {
        deliveryCollectionId = response.deliveryCollectionId;
        redirectUrl = response.redirectUrl;
        paymentData = response.paymentData;
      } else if (response?.paymentData) {
        paymentData = response.paymentData;
        deliveryCollectionId = response.paymentData.deliveryCollectionRef;
      } else if (response?.sessionSrlNo || response?.debitStatus) {
        // Raw NCHL status only — the debit is confirmed but the backend
        // hasn't finished creating the collection record yet (that only
        // happens via the websocket messageHandler). Don't treat this as
        // terminal: there's no redirect data yet, and locking the guard
        // here would silently swallow the real socket event when it
        // arrives. Reset the guard and bail so the follow-up call from
        // the socket can still do the work.
        paymentSuccessHandled.current = false;
        toast.info("Payment confirmed, finalizing collection...");
        return;
      }

      if (paymentData) {
        setPaymentResponseData(paymentData);
        toast.success("NCHL Payment successful! Delivery Collection created.");
        nchlQRRef.current?.handleCloseQR();
        setIsPaymentDone(true);

        if (deliveryCollectionId && redirectUrl) {
          toast.success("Redirecting to Delivery Collection details...", { autoClose: 1500 });
          setTimeout(() => navigate(redirectUrl), 1500);
        } else if (deliveryCollectionId) {
          const fallbackUrl = `/app/crm/delivery-collection/${deliveryCollectionId}/detail`;
          toast.success("Redirecting to Delivery Collection details...", { autoClose: 1500 });
          setTimeout(() => navigate(fallbackUrl), 1500);
        } else {
          console.error("No Delivery Collection ID found for redirect");
        }
      } else {
        // No usable payment data — let a future attempt re-fire the
        // success handler instead of permanently swallowing it.
        paymentSuccessHandled.current = false;
      }
    },
    [navigate]
  );

  useEffect(() => {
    setIsPaymentDone(false);
    setPaymentResponseData(null);
    // Allow a fresh NCHL payment attempt to re-fire onSuccess now that
    // the selected priceIds have changed (a new payment flow).
    paymentSuccessHandled.current = false;
  }, [_watchPriceIds]);

  useEffect(() => {
    if (!currentTraceId) return;
    const socket = getSocketInstance();
    if (!socket) return;
    const uniqueEvent = `payment-success/delivery-collection/${currentTraceId}`;
    const handleSuccess = (response) => {
      if (response.validationTraceId !== currentTraceId) return;
      handleNCHLPaymentSuccess(response);
    };
    socket.on(uniqueEvent, handleSuccess);
    return () => {
      // ONLY remove the listener here. Do NOT call setCurrentTraceId(null)
      // — React.StrictMode deliberately unmounts/remounts effects twice in
      // dev, and resetting currentTraceId in cleanup causes the second
      // mount's effect to see null and exit early, leaving the listener
      // permanently unregistered. The next setCurrentTraceId(newTraceId)
      // call from the click handler will trigger a re-run that swaps in
      // the new listener correctly.
      socket.off(uniqueEvent, handleSuccess);
    };
  }, [currentTraceId, handleNCHLPaymentSuccess]);

  // Fetch entities when account changes
  useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account?._id,
  ]);

  // Fetch users when account and entity are selected
  useEffect(() => {
    if (!_watchAccount?._id || !_watchEntity?._id) return;
    if (!searchQuery || searchQuery.trim().length < 3) return;

    const delayDebounce = setTimeout(() => {
      fetchShipments({
        account: _watchAccount._id,
        entity: _watchEntity._id,
        query: searchQuery.trim(),
      });
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [_watchAccount?._id, _watchEntity?._id, searchQuery, fetchShipments]);

  // Handle search for shipments with debounce - minimum 3 characters
  useEffect(() => {
    if (!_watchAccount?._id || !_watchEntity?._id) return;

    // Clear shipments if search query is empty or less than 3 characters
    if (!searchQuery || searchQuery.trim().length < 3) {
      setLocalShipments([]);
      return;
    }

    const delayDebounce = setTimeout(() => {
      fetchShipments({
        account: _watchAccount._id,
        entity: _watchEntity._id,
        query: searchQuery,
      });
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [_watchAccount?._id, _watchEntity?._id, searchQuery, fetchShipments]);

  // Update local shipments when API returns data
  useEffect(() => {
    if (shipments && Array.isArray(shipments)) {
      // Add new shipments to our collection, avoiding duplicates
      setAllFetchedShipments((prev) => {
        const existingIds = new Set(prev.map((s) => s.shipmentMongoId));
        const newShipments = shipments.filter(
          (s) => !existingIds.has(s.shipmentMongoId)
        );
        return [...prev, ...newShipments];
      });
    }
  }, [shipments]);

  // Clear bank name if collection method is not Bank
  useEffect(() => {
    if (selectedCollectionMethod?.name !== "Bank") {
      setValue("bankName", "");
    }
  }, [selectedCollectionMethod, setValue]);

  // Handle edit mode
  useEffect(() => {
    if (!deliveryCollectionDetail || loadingBankNameOptions) return;

    const bankNameValue = deliveryCollectionDetail.bankName?._id
      ? formattedBankNameOptions.find(
        (option) => option.value === deliveryCollectionDetail.bankName._id
      )?.value || ""
      : "";

    reset({
      account: deliveryCollectionDetail.account || "",
      entity: deliveryCollectionDetail.entity || "",
      deliveryType: deliveryCollectionDetail.driver
        ? "Home Delivery"
        : deliveryCollectionDetail.responsibleUser
          ? "Branch Delivery"
          : "",
      driver: deliveryCollectionDetail.driver
        ? {
          value:
            deliveryCollectionDetail.driver._id ||
            deliveryCollectionDetail.driver,
          label: deliveryCollectionDetail.driver.name
            ? `${deliveryCollectionDetail.driver.name} (${deliveryCollectionDetail.driver.driverId})`
            : "Unknown Driver",
        }
        : "",
      vehicle: deliveryCollectionDetail.vehicle_id
        ? {
          value: deliveryCollectionDetail.vehicle_id,
          label:
            deliveryCollectionDetail.vehicleName ||
            deliveryCollectionDetail.vehicle_id ||
            "Unknown Vehicle",
        }
        : "",
      responsible: deliveryCollectionDetail.responsibleUser
        ? {
          label: deliveryCollectionDetail.responsibleUser.name,
          id: deliveryCollectionDetail.responsibleUser._id,
        }
        : "",
      shipment: (() => {
        const detailMethod = entityTypeList.find(
          (item) => item._id === deliveryCollectionDetail.collectionMode
        );
        const detailIsNCHL = detailMethod?.name === "NCHL";
        const shipmentArray = deliveryCollectionDetail.shipment || [];
        return detailIsNCHL
          ? (shipmentArray[0]
            ? { _id: shipmentArray[0]._id, shipmentId: shipmentArray[0].shipmentId }
            : null)
          : shipmentArray.map((s) => ({ _id: s._id, shipmentId: s.shipmentId }));
      })(),
      priceIds:
        deliveryCollectionDetail.priceIds?.map((p) => ({
          _id: p._id || p,
          feeItem: p.feeItem || "Unknown",
          charge: p.charge || 0,
          payer: p.payer || "Unknown",
          paymentLocation: p.paymentLocation || "Unknown",
          user: p.responsibleUser || "Unknown",
          createdAt: p.createdAt || new Date(),
          shipmentId: p.shipmentId || "",
          sourceBranch: p.shipment?.sourceBranch || "",
          destinationBranch: p.shipment?.destinationBranch || "",
        })) || [],
      collectionMode: deliveryCollectionDetail.collectionMode || "",
      remittanceRemarks: deliveryCollectionDetail.remittanceRemarks || "",
      bankName: bankNameValue,
      actualAmount: deliveryCollectionDetail.actualAmount || 0,
      collectedAmount:
        deliveryCollectionDetail.collectedAmount ||
        deliveryCollectionDetail.actualAmount ||
        0,
      notes: deliveryCollectionDetail.notes || "<p>Enter Notes.</p>",
    });
  }, [
    deliveryCollectionDetail,
    loadingBankNameOptions,
    formattedBankNameOptions,
    entityTypeList,
    reset,
  ]);

  // Handle dialog confirmation
  const handleDialogConfirm = (data) => {
    setOpenDialog(false);
    const validShipmentIds = pricingData
      .filter((shipment) => selectedPriceIds.includes(shipment.price._id))
      .map((shipment) => shipment.price.shipmentId);
    const shipmentIds = isNCHL
      ? (data.shipment?._id && validShipmentIds.includes(data.shipment.shipmentId)
        ? [data.shipment._id]
        : [])
      : (Array.isArray(data.shipment)
        ? data.shipment
          .filter((s) => s._id && validShipmentIds.includes(s.shipmentId))
          .map((s) => s._id)
        : []);
    const payload = {
      ...data,
      entity: data.entity?._id || data.entity,
      account: data.account?._id || data.account,
      bankName: data.bankName || "",
      driver:
        data.deliveryType === "Home Delivery" ? data.driver?.value : undefined,
      vehicle:
        data.deliveryType === "Home Delivery" ? data.vehicle?.value : undefined,
      responsibleUser:
        data.deliveryType === "Branch Delivery"
          ? data.responsible?.id
          : undefined,
      collectedAmount: data.collectedAmount,
      shipment: shipmentIds,
      priceIds: data.priceIds?.map((p) => p._id) || [],
    };

    onSubmit(payload);
  };

  // Handle dialog cancellation
  const handleDialogCancel = () => {
    setOpenDialog(false);
    toast.error("Shipment's fee item not selected.");
  };

  const handleOnSubmit = (data) => {
    const actualAmount = Number(data.actualAmount);
    const collectedAmount = Number(data.collectedAmount);
    if (actualAmount !== collectedAmount) {
      toast.error(
        actualAmount > collectedAmount
          ? "Collection Amount is less than Actual Amount."
          : "Collection Amount is more than Actual Amount."
      );
      return;
    }
    if (!_watchPriceIds?.length) {
      toast.error("Please select at least one fee item.");
      return;
    }

    const priceShipmentIds = pricingData
      .filter((shipment) => selectedPriceIds.includes(shipment.price._id))
      .map((shipment) => shipment.price.shipmentId);
    if (isNCHL) {
      const shipmentHasNoPrice =
        _watchShipment?.shipmentId && !priceShipmentIds.includes(_watchShipment.shipmentId);
      if (shipmentHasNoPrice) {
        setInvalidShipments([_watchShipment.shipmentId]);
        setOpenDialog(true);
        return;
      }
    } else {
      const shipmentsWithoutPrice = (Array.isArray(_watchShipment) ? _watchShipment : []).filter(
        (s) => !priceShipmentIds.includes(s.shipmentId)
      );
      if (shipmentsWithoutPrice.length > 0) {
        setInvalidShipments(shipmentsWithoutPrice.map((s) => s.shipmentId));
        setOpenDialog(true);
        return;
      }
    }
    const shipmentIds = isNCHL
      ? (data.shipment?._id ? [data.shipment._id] : [])
      : (Array.isArray(data.shipment) ? data.shipment.filter((s) => s._id).map((s) => s._id) : []);
    const payload = {
      ...data,
      entity: data.entity?._id || data.entity,
      account: data.account?._id || data.account,
      remittanceRemarks: data.remittanceRemarks || "",
      bankName: data.bankName || "",
      driver:
        data.deliveryType === "Home Delivery" ? data.driver?.value : undefined,
      vehicle:
        data.deliveryType === "Home Delivery" ? data.vehicle?.value : undefined,
      responsibleUser:
        data.deliveryType === "Branch Delivery"
          ? data.responsible?.id
          : undefined,
      collectedAmount: data.collectedAmount,
      shipment: shipmentIds,
      priceIds: data.priceIds?.map((p) => p._id) || [],
    };

    onSubmit(payload);
  };

  return (
    <AppForm
      loading={
        isBusy ||
        loadingBankNameOptions ||
        loadingAccounts ||
        loadingEntities ||
        loadingUsers ||
        fetchingUsers ||
        loadingShipments
      }
    >
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value || ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                      setValue("deliveryType", "");
                      setValue("driver", "");
                      setValue("vehicle", "");
                      setValue("responsible", "");
                      setValue("shipment", null);
                      setValue("priceIds", []);
                      setSelectedPriceIds([]);
                      setInvalidShipments([]);
                      setOpenDialog(false);
                      setSearchQuery("");
                      setAllFetchedShipments([]); // CLEAR ALL
                    }}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    required
                    label="Entity"
                    value={field.value || ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("deliveryType", "");
                      setValue("driver", "");
                      setValue("vehicle", "");
                      setValue("responsible", "");
                      setValue("shipment", null);
                      setValue("priceIds", []);
                      setSelectedPriceIds([]);
                      setInvalidShipments([]);
                      setOpenDialog(false);
                      setSearchQuery("");
                      setAllFetchedShipments([]); // CLEAR ALL
                    }}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <Controller
              name="collectionMode"
              control={control}
              rules={{ required: "Please select a collection method." }}
              render={({ field }) => (
                <BaseSelect
                  fullWidth
                  label="Collection Mode"
                  options={_mappedAccountTypeList}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    const newMethod = entityTypeList.find(
                      (item) => item._id === e.target.value
                    );
                    const nowNCHL = newMethod?.name === "NCHL";
                    setValue("shipment", nowNCHL ? null : []);
                    setValue("remittanceRemarks", "");
                    setValue("deliveryType", "");
                    setValue("driver", "");
                    setValue("vehicle", "");
                    setValue("responsible", "");
                    setValue("priceIds", []);
                    setSelectedPriceIds([]);
                    setInvalidShipments([]);
                    setOpenDialog(false);
                    setSearchQuery("");
                  }}
                  error={!!errors.collectionMode}
                  helperText={errors.collectionMode?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="shipment"
              control={control}
              rules={{
                required: isNCHL
                  ? "Please select a shipment."
                  : "Please select at least one shipment.",
              }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  multiple={!isNCHL}
                  label="Shipment ID"
                  required
                  value={isNCHL ? (value || null) : (Array.isArray(value) ? value : [])}
                  onInputChange={(_, newInputValue) => {
                    setSearchQuery(newInputValue);
                  }}
                  onChange={(_, data) => {
                    if (isNCHL) {
                      setValue("shipment", data);
                      setValue("priceIds", []);
                      setSelectedPriceIds([]);
                      setInvalidShipments([]);
                      setOpenDialog(false);
                      setValue("remittanceRemarks", data?.shipmentId || "");
                      return;
                    }
                    // Multi-select mode: enforce same driver/vehicle or same user
                    const previousShipments = Array.isArray(value) ? value : [];
                    const newShipments = data || [];
                    const removedShipments = previousShipments.filter(
                      (prevShip) =>
                        !newShipments.some((newShip) => newShip._id === prevShip._id)
                    );
                    if (previousShipments.length === 0) {
                      onChange(data);
                      setValue("priceIds", []);
                      setSelectedPriceIds([]);
                      setInvalidShipments([]);
                      setOpenDialog(false);
                      return;
                    }
                    const firstShipment = allFetchedShipments.find(
                      (delivery) => delivery.shipmentMongoId === previousShipments[0]._id
                    );
                    if (!firstShipment) {
                      onChange(data);
                      return;
                    }
                    const isFirstDriverBased =
                      firstShipment?.driverId || firstShipment?.vehicle_id;
                    const isFirstUserBased = firstShipment?.responsibleUser;
                    const validData = [];
                    data.forEach((newShipment) => {
                      const matchingShipment = allFetchedShipments.find(
                        (delivery) => delivery.shipmentMongoId === newShipment._id
                      );
                      if (!matchingShipment) {
                        validData.push(newShipment);
                        return;
                      }
                      if (isFirstDriverBased) {
                        if (
                          matchingShipment.driverId === firstShipment.driverId &&
                          matchingShipment.vehicle_id === firstShipment.vehicle_id
                        ) {
                          validData.push(newShipment);
                        } else {
                          toast.error(
                            `Shipments should be of same driver and vehicle. Cannot select shipment ${newShipment.shipmentId}.`
                          );
                        }
                      } else if (isFirstUserBased) {
                        if (matchingShipment.responsibleUser === firstShipment.responsibleUser) {
                          validData.push(newShipment);
                        } else {
                          toast.error(
                            `Shipments should be of same user. Cannot select shipment ${newShipment.shipmentId}.`
                          );
                        }
                      } else {
                        if (
                          !matchingShipment.driverId &&
                          !matchingShipment.vehicle_id &&
                          !matchingShipment.responsibleUser
                        ) {
                          validData.push(newShipment);
                        } else {
                          toast.error(
                            `Cannot mix Free Unloading deliveries with assigned deliveries.`
                          );
                        }
                      }
                    });
                    if (validData.length > 0 || data.length === 0) {
                      onChange(validData);
                      if (removedShipments.length > 0) {
                        const removedShipmentIds = removedShipments.map((ship) => ship._id);
                        const updatedSelectedPriceIds = selectedPriceIds.filter((priceId) => {
                          const priceItem = pricingData.find((p) => p.price._id === priceId);
                          return (
                            !priceItem || !removedShipmentIds.includes(priceItem.shipmentId)
                          );
                        });
                        setSelectedPriceIds(updatedSelectedPriceIds);
                        const updatedPriceIds = pricingData
                          .filter((item) => updatedSelectedPriceIds.includes(item.price._id))
                          .map((item) => item.price);
                        setValue("priceIds", updatedPriceIds);
                        const totalAmount = updatedPriceIds.reduce(
                          (sum, price) => sum + (price.charge || 0),
                          0
                        );
                        setValue("actualAmount", totalAmount);
                        setValue("collectedAmount", totalAmount);
                      }
                    }
                  }}
                  getOptionLabel={(option) => option?.shipmentId || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id && value?._id
                      ? option._id.toString() === value._id.toString()
                      : false
                  }
                  options={availableShipments}
                  loading={loadingShipments}
                  loadingText="Loading Shipments..."
                  noOptionsText={
                    !searchQuery || searchQuery.trim().length < 3
                      ? "Type at least 3 characters to search"
                      : "No shipments found"
                  }
                  helperText={errors?.shipment?.message}
                  error={!!errors?.shipment}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="deliveryType"
              control={control}
              rules={{ required: "Delivery Type is required" }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors?.deliveryType}
                  disabled
                >
                  <InputLabel id="delivery-type-label">
                    Delivery Type
                  </InputLabel>
                  <Select
                    labelId="delivery-type-label"
                    label="Delivery Type"
                    value={field.value || ""}
                    onChange={(e) => {
                      field.onChange(e);
                      setValue("driver", "");
                      setValue("vehicle", "");
                      setValue("responsible", "");
                      setValue("priceIds", []);
                      setSelectedPriceIds([]);
                      setInvalidShipments([]);
                      setOpenDialog(false);
                    }}
                    required
                  >
                    <MenuItem value="Home Delivery">Driver Delivery</MenuItem>
                    <MenuItem value="Branch Delivery">User Delivery</MenuItem>
                    <MenuItem value="Free Unloading Delivery">
                      Free Unloading Delivery
                    </MenuItem>
                  </Select>
                  {errors.deliveryType && (
                    <FormHelperText error>
                      {errors.deliveryType.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          {_watchDeliveryType === "Home Delivery" && (
            <>
              <Grid item md={6}>
                <Controller
                  name="driver"
                  control={control}
                  rules={{
                    required:
                      _watchDeliveryType === "Home Delivery"
                        ? "Please select a driver."
                        : false,
                  }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      required={_watchDeliveryType === "Home Delivery"}
                      label="Vehicle"
                      value={field.value || ""}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue("priceIds", []);
                        setSelectedPriceIds([]);
                        setInvalidShipments([]);
                        setOpenDialog(false);
                      }}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      options={[]}
                      disabled
                      helperText={errors?.vehicle?.message}
                      error={!!errors?.vehicle}
                    />
                  )}
                />
              </Grid>
            </>
          )}
          {_watchDeliveryType === "Branch Delivery" && (
            <Grid item md={6}>
              <Controller
                name="responsible"
                control={control}
                rules={{
                  required:
                    _watchDeliveryType === "Branch Delivery"
                      ? "Please select a delivered user."
                      : false,
                }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required={_watchDeliveryType === "Branch Delivery"}
                    label="Delivered User"
                    loading={loadingUsers || fetchingUsers}
                    loadingText="Loading Delivered Users..."
                    options={_mappedUsers}
                    value={field.value || null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("priceIds", []);
                      setSelectedPriceIds([]);
                      setInvalidShipments([]);
                      setOpenDialog(false);
                    }}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    error={!!errors?.responsible}
                    helperText={errors?.responsible?.message}
                    disabled
                  />
                )}
              />
            </Grid>
          )}

          {selectedCollectionMethod?.name === "Bank" && (
            <Grid item xs={6}>
              <Controller
                name="bankName"
                control={control}
                rules={{ required: "Please select a bank name." }}
                render={({ field }) => (
                  <BaseSelect
                    fullWidth
                    label="Bank Name"
                    options={formattedBankNameOptions}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    getOptionLabel={(option) => option.label || ""}
                    getOptionValue={(option) => option.value || ""}
                    error={!!errors.bankName}
                    helperText={errors.bankName?.message}
                    loading={loadingBankNameOptions}
                    loadingText="Loading Bank Names..."
                  />
                )}
              />
            </Grid>
          )}
          <Grid item md={6}>
            <Controller
              name="actualAmount"
              control={control}
              rules={{
                required: "Amount is required.",
                min: { value: 0, message: "Amount must be non-negative." },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Actual Amount"
                  fullWidth
                  size="small"
                  required
                  type="number"
                  error={!!errors?.actualAmount}
                  helperText={errors?.actualAmount?.message}
                  disabled
                />
              )}
            />
          </Grid>

          <Grid item md={6} sx={{ display: "none" }}>
            <Controller
              name="collectedAmount"
              control={control}
              rules={{
                required: "Amount is required.",
                min: { value: 0, message: "Amount must be non-negative." },
                validate: (value) =>
                  Number(value) === Number(_watchActualAmount)
                    ? true
                    : "Collected Amount must match Actual Amount.",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Collected Amount"
                  fullWidth
                  size="small"
                  required
                  type="number"
                  error={!!errors?.collectedAmount}
                  helperText={errors?.collectedAmount?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="remittanceRemarks"
              control={control}
              rules={{ required: "Please enter remittance remarks." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Remittance Remarks"
                  variant="outlined"
                  size="small"
                  required
                  error={!!errors.remittanceRemarks}
                  helperText={errors.remittanceRemarks?.message}
                  inputProps={{ maxLength: 255 }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            {isNCHL && !isPaymentDone && unpaidPriceIds.length > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <Button
                  variant="outlined"
                  color="success"
                  onClick={async () => {
                    const traceId = await nchlQRRef.current?.handleGenerateQR();
                    if (traceId) setCurrentTraceId(traceId);
                  }}
                  disabled={
                    isBusy ||
                    !_watchPriceIds?.length ||
                    Number(_watchCollectedAmount) <= 0
                  }
                >
                  Pay Via NCHL
                </Button>
                <NCHLQR
                  ref={nchlQRRef}
                  addedUser={loggedInUser?._id}
                  amount={Number(_watchActualAmount) || 0}
                  priceIds={unpaidPriceIds}
                  shipmentId={_watchShipment?.shipmentId || ""}
                  onSuccess={handleNCHLPaymentSuccess}
                />
              </div>
            )}
          </Grid>
          {((isNCHL && _watchShipment?._id) ||
            (!isNCHL && Array.isArray(_watchShipment) && _watchShipment.length > 0)) && (
              <Grid item xs={12}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Checkbox
                            checked={
                              pricingData.length > 0 &&
                              selectedPriceIds.length === pricingData.length
                            }
                            indeterminate={
                              selectedPriceIds.length > 0 &&
                              selectedPriceIds.length < pricingData.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Select all
                                const allPriceIds = pricingData.map(
                                  (item) => item.price._id
                                );
                                setSelectedPriceIds(allPriceIds);
                                setValue(
                                  "priceIds",
                                  pricingData.map((item) => item.price)
                                );

                                // Update amounts
                                const totalAmount = pricingData.reduce(
                                  (sum, item) => {
                                    return sum + (item.price.charge || 0);
                                  },
                                  0
                                );
                                setValue("actualAmount", totalAmount);
                                setValue("collectedAmount", totalAmount);
                              } else {
                                // Deselect all
                                setSelectedPriceIds([]);
                                setValue("priceIds", []);
                                setValue("actualAmount", 0);
                                setValue("collectedAmount", 0);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>Fee Item</TableCell>
                        <TableCell>Charge Type</TableCell>
                        <TableCell>Charge</TableCell>
                        <TableCell>Shipment ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pricingData.map((shipment) => (
                        <TableRow
                          key={shipment.price._id}
                          sx={{
                            border: invalidShipments.includes(shipment.shipmentId)
                              ? "2px solid red"
                              : "none",
                          }}
                        >
                          <TableCell>
                            <Checkbox
                              checked={selectedPriceIds.includes(
                                shipment.price._id
                              )}
                              onChange={() => {
                                handlePriceSelection(shipment.price);

                                // Update amounts immediately when individual checkbox changes
                                const isSelected = selectedPriceIds.includes(
                                  shipment.price._id
                                );
                                let updatedPriceIds;

                                if (isSelected) {
                                  updatedPriceIds = selectedPriceIds.filter(
                                    (id) => id !== shipment.price._id
                                  );
                                } else {
                                  updatedPriceIds = [
                                    ...selectedPriceIds,
                                    shipment.price._id,
                                  ];
                                }

                                const updatedPrices = pricingData
                                  .filter((item) =>
                                    updatedPriceIds.includes(item.price._id)
                                  )
                                  .map((item) => item.price);

                                const totalAmount = updatedPrices.reduce(
                                  (sum, price) => {
                                    return sum + (price.charge || 0);
                                  },
                                  0
                                );

                                setValue("actualAmount", totalAmount);
                                setValue("collectedAmount", totalAmount);
                              }}
                            />
                          </TableCell>
                          <TableCell>{shipment.price.feeItem}</TableCell>
                          <TableCell>{shipment.price.chargeType}</TableCell>
                          <TableCell>{shipment.price.charge}</TableCell>
                          <TableCell>{shipment.shipmentId}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          <Grid item md={12}>
            <div
              style={{ color: "gray", fontSize: 15, marginBottom: ".75rem" }}
            >
              Notes
            </div>
            <Controller
              name="notes"
              control={control}
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  initialValue={
                    deliveryCollectionDetail?.notes
                      ? deliveryCollectionDetail.notes
                      : "<p>Enter a note</p>"
                  }
                  onChange={onChange}
                  height={200}
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          disabled={isBusy || !_watchPriceIds?.length || isNCHL}
          onClick={handleSubmit(handleOnSubmit)}
        >
          {deliveryCollectionDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
      <Dialog open={openDialog} onClose={handleDialogCancel}>
        <DialogTitle>Shipment Fee Item Missing</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The following shipments have no selected fee items:{" "}
            {invalidShipments
              .map((id) => {
                const shipment = availableShipments.find((s) => s._id === id);
                return shipment ? shipment.shipmentId : id;
              })
              .join(", ")}
            . Want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDialogConfirm({ ...control._formValues })}
            autoFocus
          >
            Yes
          </Button>
          <Button onClick={handleDialogCancel}>No</Button>
        </DialogActions>
      </Dialog>
    </AppForm>
  );
};

export default DeliveryCollectionByShipmentForm;